"use client"
import { FaArrowRight, FaArrowLeft, FaEye } from "react-icons/fa"
import { getStorage, ref as storageRef, getDownloadURL } from "firebase/storage"
import { storage } from "../Firebase/firebase-config"  // Make sure storage is exported

const OrderFormStep3 = ({
  handleStep3Submit,
  handleSkipStep3,
  handleBackToStep2,
  documentFiles,
  documentUploaded,
  existingDocuments,
  handleDocumentFileChange,
  tdsTcsType,
  setTdsTcsType,
  tdsTcsFile,
  setTdsTcsFile,
  error,
  clearError,
}) => {
  const documents = [
    { key: "gstCopy", label: "GST Copy", required: true },
    { key: "panCard", label: "PAN Card", required: true },
    { key: "workOrder", label: "Work Order", required: true },
    { key: "letterHead", label: "Letter Head", required: true },
    { key: "tanNumber", label: "TAN Number", required: true },
    { key: "cinCertificate", label: "CIN Certificate", required: true },
    { key: "partnershipDeed", label: "Partnership Deed", required: true },
    { key: "purchaseOrder", label: "Purchase Order", required: false },
    { key: "cancelCheque", label: "Cancel Cheque (Optional)", required: false },
  ]

  const isUploaded = (key) => documentUploaded[key] || existingDocuments[key]
  const hasExistingPath = (key) => !!existingDocuments[key]

  const getButtonText = (key) => {
    if (documentFiles[key]) return "✓ Selected (Will Replace)"
    if (existingDocuments[key]) return "Replace"
    return "Choose File"
  }

  // Generate fresh download URL and open in new tab
  const openDocument = async (storagePath) => {
    if (!storagePath) {
      alert("No document found.")
      return
    }

    try {
      const fileRef = storageRef(storage, storagePath)
      const url = await getDownloadURL(fileRef)
      window.open(url, "_blank", "noopener,noreferrer")
    } catch (error) {
      console.error("Error opening document:", error)
      alert("Unable to open document. Please try again later.")
    }
  }

  const handleTdsTcsFileChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      setTdsTcsFile(file)
      clearError()
    }
  }

  const getTdsTcsButtonText = () => {
    if (tdsTcsFile) return "✓ Selected (Will Replace)"
    if (existingDocuments[tdsTcsType]) return "Replace"
    return `Upload ${tdsTcsType}`
  }

  const isTdsTcsComplete = tdsTcsType !== "none" && (tdsTcsFile || existingDocuments[tdsTcsType])

  return (
    <form onSubmit={handleStep3Submit} className="doc-upload-section">
      <h2 className="doc-upload-title">Document Upload</h2>

      {error && <div className="doc-error-alert">⚠️ {error}</div>}

      <div className="doc-grid">
        {documents.map((doc) => (
          <div key={doc.key} className="doc-item">
            <label className="doc-label">
              {doc.label}
              {doc.required && <span className="doc-required"> *</span>}
            </label>

            <div className="doc-file-controls">
              {/* View Button - Only if path exists */}
              {hasExistingPath(doc.key) && (
                <button
                  type="button"
                  onClick={() => openDocument(existingDocuments[doc.key])}
                  className="doc-view-btn"
                >
                  <FaEye /> View
                </button>
              )}

              {/* Upload / Replace Button */}
              <div className="doc-file-wrapper">
                <input
                  type="file"
                  id={doc.key}
                  accept=".png,.jpg,.jpeg,.pdf"
                  onChange={(e) => handleDocumentFileChange(e, doc.key)}
                  className="doc-file-input"
                />
                <label
                  htmlFor={doc.key}
                  className={`doc-file-btn ${isUploaded(doc.key) || documentFiles[doc.key] ? "uploaded" : ""}`}
                >
                  {getButtonText(doc.key)}
                </label>
              </div>
            </div>
          </div>
        ))}

        {/* TDS / TCS - REQUIRED */}
        <div className="tds-tcs-group">
          <label className="doc-label">
            TDS / TCS <span className="doc-required"> *</span>
          </label>

          <div className="tds-tcs-radios">
            <label className="tds-tcs-radio-label">
              <input
                type="radio"
                name="tdsTcs"
                value="TDS"
                checked={tdsTcsType === "TDS"}
                onChange={(e) => setTdsTcsType(e.target.value)}
              />
              TDS
            </label>
            <label className="tds-tcs-radio-label">
              <input
                type="radio"
                name="tdsTcs"
                value="TCS"
                checked={tdsTcsType === "TCS"}
                onChange={(e) => setTdsTcsType(e.target.value)}
              />
              TCS
            </label>
          </div>

          {tdsTcsType !== "none" && (
            <div className="doc-file-controls">
              {/* View Button for TDS/TCS */}
              {existingDocuments[tdsTcsType] && (
                <button
                  type="button"
                  onClick={() => openDocument(existingDocuments[tdsTcsType])}
                  className="doc-view-btn"
                >
                  <FaEye /> View
                </button>
              )}

              {/* Upload / Replace */}
              <div className="doc-file-wrapper">
                <input
                  type="file"
                  id="tdsTcsFile"
                  accept=".png,.jpg,.jpeg,.pdf"
                  onChange={handleTdsTcsFileChange}
                  className="doc-file-input"
                />
                <label
                  htmlFor="tdsTcsFile"
                  className={`doc-file-btn ${isTdsTcsComplete ? "uploaded" : ""}`}
                >
                  {getTdsTcsButtonText()}
                </label>
              </div>
            </div>
          )}

          {tdsTcsType === "none" && (
            <div className="doc-tds-warning">
              Please select TDS or TCS (Required)
            </div>
          )}
        </div>
      </div>

      <div className="doc-notes">
        Max file size: <strong className="doc-note-highlight">5MB</strong> (PNG, JPG, JPEG, PDF)
      </div>
      <div className="doc-notes">
        Already uploaded? Use "View" to check or "Replace" to update
      </div>

      <div className="doc-action-buttons">
        <button type="button" className="doc-back-btn" onClick={handleBackToStep2}>
          <FaArrowLeft /> Back
        </button>
      
        <button type="submit" className="doc-next-btn">
          Next <FaArrowRight />
        </button>
      </div>
    </form>
  )
}

export default OrderFormStep3